import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import dayjs, { Dayjs } from 'dayjs'
import { AuthFormBox } from './__styled'
import { Input, MyDatePicker } from '@/components'
import { validateFirstName, validateLastName } from '@/utils/helper'

interface FormProps {
  onSubmit: (firstName: string, lastName: string, dob: Dayjs) => void
}

export default function SignUpForm2({ onSubmit }: FormProps) {
  const [firstName, setFirstName] = useState('') // Email or Phone number
  const [lastName, setLastName] = useState('')
  const [dob, setDOB] = useState<Dayjs | null>(dayjs().subtract(13, 'year'))
  const [editing, setEditing] = useState(false)

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!firstName.length) {
      error.firstName = 'First Name is required'
    } else if (!validateFirstName(firstName)) {
      error.firstName = 'Enter Valid First Name'
    }
    if (!lastName.length) {
      error.lastName = 'Last Name is required'
    } else if (!validateLastName(lastName)) {
      error.lastName = 'Enter Valid Last Name'
    }
    if (!dayjs(dob).isValid()) {
      error.dob = 'Date of Birth is invalid'
    }
    return error
  }, [firstName, lastName, dob])

  const isFormValid = !formError.firstName && !formError.lastName && !formError.dob

  const onChangeFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditing(true)
    setFirstName(e.target.value)
  }

  const onChangeLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditing(true)
    setLastName(e.target.value)
  }

  const onChangeDOB = (date: Dayjs | null) => {
    setEditing(true)
    setDOB(date)
  }

  const onContinue = () => {
    if (isFormValid && dob) onSubmit(firstName, lastName, dob)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        p: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '440px' }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant='h4'
            fontWeight={700}
            color='#1a1a1a'
            mb={1}
            sx={{ fontSize: { xs: '28px', md: '32px' } }}
          >
            Sign Up
          </Typography>
          <Typography variant='body1' color='#666666' lineHeight={1.6}>
            Sign up to see shared topics and media from your connections
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Input
            value={firstName}
            placeholder='First Name'
            onChange={onChangeFirstName}
            errorText={formError.firstName}
            isError={editing && !!formError.firstName}
            name='first name'
          />
          <Input
            value={lastName}
            placeholder='Last Name'
            onChange={onChangeLastName}
            errorText={formError.lastName}
            isError={editing && !!formError.lastName}
            name='last name'
          />
          <MyDatePicker
            errorText={formError.dob}
            isError={editing && !!formError.dob}
            onChange={onChangeDOB}
            value={dob}
          />

          {/* Terms Notice */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant='body2' color='#666666' fontSize='13px' lineHeight={1.6}>
              By continuing, you agree to our{' '}
              <Link to='/terms' style={{ textDecoration: 'none' }}>
                <Typography
                  component='span'
                  variant='body2'
                  sx={{
                    color: '#1a1a1a',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Terms of Services, Privacy Policy & Content Policy
                </Typography>
              </Link>
            </Typography>
          </Box>

          {/* Continue Button */}
          <Button
            variant='contained'
            size='large'
            fullWidth
            disabled={!isFormValid}
            onClick={onContinue}
            sx={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              py: 1.5,
              mt: 1,
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999999',
              },
            }}
          >
            Continue
          </Button>

          {/* Sign In Link */}
          <Box display='flex' justifyContent='center' alignItems='center' gap={0.5} mt={1}>
            <Typography variant='body2' color='#666666'>
              Got an account?
            </Typography>
            <Link to='/login' style={{ textDecoration: 'none' }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#1a1a1a',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign In
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
